import { createContext, useState, useRef, useEffect, useContext } from "react";
import { getPlayableAudioUrl } from "../utils/audioUrlHelper";
import { listenEvent } from "@/utilities/eventDispatcher";
import React from 'react';

interface AudioContextType {
  currentTrack: any;
  currentTrackInfo: any;
  isPlaying: boolean;
  isPlayerVisible: boolean;
  isAudioPlayerOpened: boolean;
  trackOptionsDrawerOpen: boolean;
  audioRef: React.RefObject<HTMLAudioElement>;
  showAudioPlayer: () => void;
  hideAudioPlayer: () => void;
  toggleAudioPlayerOpen: () => void;
  playTrack: (track: any, trackInfo?: any) => Promise<void>;
  setAudio: (audio: any) => void;
  pauseAudio: () => void;
  stopAudio: () => void;
  seekAudio: (time: number) => void;
  playAudio: () => Promise<void>;
  setTrackOptionsDrawerOpen: (open: boolean) => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const useAudioContext = () => {
    const context = useContext(AudioContext);
    if (!context) {
        throw new Error("useAudioContext must be used within an AudioProvider");
    }
    return context;
};

interface AudioProviderProps {
  children: React.ReactNode;
}

export const AudioProvider: React.FC<AudioProviderProps> = ({ children }) => {
    const [currentTrack, setCurrentTrack] = useState(null);
    const [currentTrackInfo, setCurrentTrackInfo] = useState<any>({}); 
    const [isPlaying, setIsPlaying] = useState(false);
    const [, setAllowMultiple] = useState(false); // For playing ideas/audio snippets while main track plays
    const [loadedAudioUrl, setLoadedAudioUrl] = useState(null); // Store the original audio URL that's currently loaded
    

    const [isPlayerVisible, setIsPlayerVisible] = useState(false);
    const [isAudioPlayerOpened, setisAudioPlayerOpened] = useState(false);
    const [trackOptionsDrawerOpen, setTrackOptionsDrawerOpen] = useState(false);


    const audioRef = useRef(null);

    useEffect(() => {

        const audio = audioRef.current;
        const handleEnd = () => setIsPlaying(false);
        audio.addEventListener("ended", handleEnd);

        return () => {
            audio.removeEventListener("ended", handleEnd);
        };
    }, []);

    const showAudioPlayer = () => {
        console.log("🔹 Updating isPlayerVisible to true");
        setIsPlayerVisible(true);
    };
    
    const hideAudioPlayer = () => {
        console.log("🔹 Updating isPlayerVisible to false");
        stopAudio(); // Stop music when closing
        setIsPlayerVisible(false);
    };

    const toggleAudioPlayerOpen = () => {
        setisAudioPlayerOpened(prev => !prev);
        setTrackOptionsDrawerOpen(false);
    }

    // Sync with Layout's audio player state via events
    useEffect(() => {
        const audioPlayerToggle = listenEvent("AudioPlayerToggle", () =>
            setisAudioPlayerOpened((prev) => !prev)
        );
        const audioPlayerOpen = listenEvent("AudioPlayerOpen", () =>
            setisAudioPlayerOpened(true)
        );
        const audioPlayerClose = listenEvent("AudioPlayerClose", () =>
            setisAudioPlayerOpened(false)
        );

        return () => {
            audioPlayerToggle();
            audioPlayerOpen();
            audioPlayerClose();
        };
    }, []);

    useEffect(() => {
        const audio = audioRef.current;

        const handleEnd = () => setIsPlaying(false);
        audio.addEventListener("ended", handleEnd);

        return () => {
            audio.removeEventListener("ended", handleEnd);
        };
    }, []);

    useEffect(() => {
        if ("mediaSession" in navigator && currentTrackInfo?.audio_url) {
            navigator.mediaSession.metadata = new MediaMetadata({
                title: currentTrackInfo.title,
                artist: currentTrackInfo.author_name || "Unknown Artist",
                album: currentTrackInfo.type || "Lyric Boost",
                artwork: [
                    { src: currentTrackInfo.thumbnail || "/cover-art/placeholder-cover-art.jpg", sizes: "512x512", type: "image/jpeg" }
                ]
            });

            navigator.mediaSession.setActionHandler("play", () => playAudio());
            navigator.mediaSession.setActionHandler("pause", () => pauseAudio());
        }
    }, [currentTrackInfo]);

    const setAudio = (audio) => {
        if (!audio) {
            console.log("🛑 No audio provided, resetting track.");
            setCurrentTrack(null);
            setCurrentTrackInfo({});
            stopAudio();
            return;
        }
    
        setCurrentTrack(audio);
        setCurrentTrackInfo({ 
            title: audio.title,
            type: audio.type,
            bpm: audio.bpm,
            author_name: audio.artist_name,
            audio_url: audio.audio_url,
        });
    };
    
    
    const playAudio = async () => {
        const audio = currentTrack;
        if (!audio?.audio_url) {
            console.warn("⚠️ No audio URL found, cannot play.");
            return;
        }
    
        console.log("🔵 playAudio called with:", audio);
    
        try {
            const audioElement = audioRef.current;
            
            // Check if this is the same track that's already loaded
            if (loadedAudioUrl === audio.audio_url) {
                console.log("▶️ Resuming existing track from position:", audioElement.currentTime);
                // Just resume playback, don't reload
                audioElement
                    .play()
                    .then(() => {
                        console.log("✅ Audio is playing.");
                        setIsPlaying(true);
                    })
                    .catch((error) => {
                        console.error("❌ Error playing audio:", error);
                    });
                return;
            }
            
            // Different track - need to load it
            console.log("⏳ Loading new track:", audio.audio_url);
            
            // Get playable URL (converts storage paths to signed URLs)
            const playableUrl = await getPlayableAudioUrl(audio.audio_url);
            
            if (!playableUrl) {
                console.error("❌ Failed to get playable URL for:", audio.audio_url);
                return;
            }
            
            audioElement.src = playableUrl;
            audioElement.load();
            setLoadedAudioUrl(audio.audio_url); // Remember which track is loaded
    
            audioElement
                .play()
                .then(() => {
                    console.log("✅ Audio is playing.");
                    setIsPlaying(true);
                })
                .catch((error) => {
                    console.error("❌ Error playing audio:", error);
                });
        } catch (error) {
            console.error("❌ Error in playAudio:", error);
        }
    };    

    const pauseAudio = () => {
        audioRef.current.pause();
        setIsPlaying(false);
    };

    const stopAudio = () => {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlaying(false);
        setLoadedAudioUrl(null); // Clear loaded track
    };

    
    const seekAudio = (time) => {
        audioRef.current.currentTime = time;
    };

    const togglePlayback = () => {
        if (isPlaying) pauseAudio();
        else playAudio();
    };

    const playTrackHandler = async (track: any, trackInfo?: any) => {
        if (!track) {
            console.warn("⚠️ No track provided to playTrack");
            return;
        }
        
        console.log("🎵 playTrack called with:", track);
        
        // Set the audio track
        setCurrentTrack(track);
        setCurrentTrackInfo({ 
            title: track.title,
            type: track.type || track.track_type,
            bpm: track.bpm,
            author_name: track.artist_name || track.author_name,
            audio_url: track.audio_url,
        });
        
        // Wait a tick for state to update, then play
        setTimeout(async () => {
            try {
                const playableUrl = await getPlayableAudioUrl(track.audio_url);
                if (!playableUrl) {
                    console.error("❌ Failed to get playable URL for:", track.audio_url);
                    return;
                }
                
                const audioElement = audioRef.current;
                if (audioElement.src !== playableUrl) {
                    console.log("⏳ Loading new track:", playableUrl);
                    audioElement.src = playableUrl;
                    audioElement.load();
                }
                
                await audioElement.play();
                console.log("✅ Track playing:", track.title);
                setIsPlaying(true);
            } catch (error) {
                console.error("❌ Error playing track:", error);
            }
        }, 0);
    };

    return (
       <AudioContext.Provider value={{
        audioRef, currentTrack, currentTrackInfo, isPlaying, playAudio, pauseAudio, stopAudio, seekAudio, 
    trackOptionsDrawerOpen, setTrackOptionsDrawerOpen,
    isPlayerVisible, showAudioPlayer, hideAudioPlayer, isAudioPlayerOpened, toggleAudioPlayerOpen,
    setAudio,
    playTrack: playTrackHandler
}}> 
            <audio ref={audioRef} hidden />
            {children}
        </AudioContext.Provider>
    );
};

export default AudioContext;
